import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  path: string;
  type?: string;
  faqSchema?: Array<{ question: string; answer: string }>;
  noindex?: boolean;
  ogImage?: string;
  imageAlt?: string;
  keywords?: string[];
  articleMeta?: {
    publishedTime: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
    author?: string;
  };
  extraJsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  articleSchema?: {
    headline: string;
    description: string;
    datePublished: string;
    dateModified?: string;
    author?: string;
    image?: string;
    section?: string;
    keywords?: string[];
    wordCount?: number;
  };

  serviceSchema?: {
    name: string;
    description: string;
    serviceType: string;
    areaServed: Array<{ name: string; type?: string }>;
    category?: string;
  };
}


const SITE_URL = "https://www.kinshipcarecollective.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-kinship.jpg`;
const DEFAULT_IMAGE_ALT = "Kinship Care Collective — senior and elder companion care in Nashville, Tennessee";

/** Trim to `max` characters on a word boundary so search results never truncate mid-word. */
const clamp = (value: string, max: number) => {
  const text = value.trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const boundary = cut.lastIndexOf(" ");
  return `${(boundary > max * 0.6 ? cut.slice(0, boundary) : cut).replace(/[\s,;:—-]+$/, "")}…`;
};

const toAbsolute = (url?: string) =>
  !url ? undefined : url.startsWith("http") ? url : `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;

const SEO = ({ title, description, path, type = "website", faqSchema, noindex, ogImage, imageAlt, keywords, articleMeta, extraJsonLd, articleSchema, serviceSchema }: SEOProps) => {
  const hasBrand = path === "/" || title.toLowerCase().includes("kinship") || title.includes(" | ");
  const rawTitle = hasBrand ? title : `${clamp(title, 49)} | Kinship`;
  const fullTitle = clamp(rawTitle, 60);
  const metaDescription = clamp(description, 158);
  const canonical = `${SITE_URL}${path}`;
  const image = toAbsolute(ogImage) ?? DEFAULT_OG_IMAGE;
  const alt = imageAlt ?? DEFAULT_IMAGE_ALT;

  /**
   * index.html ships static og/description tags as a fallback for crawlers that
   * do not run JS. Helmet cannot dedupe tags it did not render, so once React is
   * mounted we drop the static copies — otherwise every route reports duplicate
   * meta descriptions and a homepage social preview.
   */
  useEffect(() => {
    const stale = document.head.querySelectorAll(
      'meta[name="description"]:not([data-rh]), meta[property^="og:"]:not([data-rh]), meta[name^="twitter:"]:not([data-rh])',
    );
    stale.forEach((el) => el.remove());
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <meta name="author" content={articleMeta?.author ?? "Kinship Care Collective"} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Kinship Care Collective" />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={alt} />
      {articleMeta && (
        <meta property="article:published_time" content={new Date(articleMeta.publishedTime).toISOString()} />
      )}
      {articleMeta?.modifiedTime && (
        <meta property="article:modified_time" content={new Date(articleMeta.modifiedTime).toISOString()} />
      )}
      {articleMeta?.section && <meta property="article:section" content={articleMeta.section} />}
      {articleMeta?.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      {articleMeta && (
        <meta property="article:publisher" content="https://www.kinshipcarecollective.com" />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={alt} />
      <meta name="geo.region" content="US-TN" />
      <meta name="geo.placename" content="Nashville" />
      {!noindex && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${canonical}#webpage`,
            url: canonical,
            name: fullTitle,
            description,
            inLanguage: "en-US",
            isPartOf: { "@id": `${SITE_URL}/#website` },
            about: { "@id": `${SITE_URL}/#localbusiness` },
            primaryImageOfPage: { "@type": "ImageObject", url: image, caption: alt },
            ...(articleMeta?.publishedTime
              ? { datePublished: new Date(articleMeta.publishedTime).toISOString() }
              : {}),
            ...(articleMeta?.modifiedTime
              ? { dateModified: new Date(articleMeta.modifiedTime).toISOString() }
              : {}),
          })}
        </script>
      )}
      {faqSchema && faqSchema.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqSchema.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${canonical}#article`,
            headline: clamp(articleSchema.headline, 110),
            description: articleSchema.description,
            datePublished: new Date(articleSchema.datePublished).toISOString(),
            dateModified: new Date(articleSchema.dateModified ?? articleSchema.datePublished).toISOString(),
            image: {
              "@type": "ImageObject",
              url: articleSchema.image ?? image,
              width: 1200,
              height: 630,
            },
            ...(articleSchema.section ? { articleSection: articleSchema.section } : {}),
            ...(articleSchema.keywords?.length ? { keywords: articleSchema.keywords.join(", ") } : {}),
            ...(articleSchema.wordCount ? { wordCount: articleSchema.wordCount } : {}),
            inLanguage: "en-US",
            isAccessibleForFree: true,
            author: {
              "@type": "Organization",
              name: articleSchema.author ?? "Kinship Care Collective",
              url: SITE_URL,
            },
            publisher: {
              "@type": "Organization",
              name: "Kinship Care Collective",
              url: SITE_URL,
              logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/icon-512.png`,
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${canonical}#webpage` },
          })}
        </script>
      )}
      {extraJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(extraJsonLd)
              ? { "@context": "https://schema.org", "@graph": extraJsonLd }
              : extraJsonLd,
          )}
        </script>
      )}

      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: serviceSchema.name,
            description: serviceSchema.description,
            url: canonical,
            serviceType: serviceSchema.serviceType,
            category: serviceSchema.category ?? "Non-Medical Home Care",
            provider: {
              "@type": "LocalBusiness",
              "@id": `${SITE_URL}/#localbusiness`,
            },
            areaServed: serviceSchema.areaServed.map((place) => ({
              "@type": place.type ?? "City",
              name: place.name,
              containedInPlace: { "@type": "State", name: "Tennessee" },
            })),
            availableChannel: {
              "@type": "ServiceChannel",
              servicePhone: {
                "@type": "ContactPoint",
                telephone: "+1-615-807-0618",
                contactType: "sales",
                availableLanguage: "English",
              },
              serviceUrl: canonical,
            },
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
